// ─── Customer Complaints page (الشكاوي) ───
// Customer writes a complaint → sent to admin. Customer info (name,
// market, iPad id, phone) is attached server-side from the JWT.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowRight,
  Send,
  Loader2,
  MessageCircle,
  CheckCircle2,
  Clock3,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  if (status === "resolved") {
    return (
      <span className="inline-flex items-center gap-1 bg-[#DCFCE7] text-[#15803d] text-[10px] font-bold rounded-full px-2 py-0.5">
        <CheckCircle2 className="w-3 h-3" /> تم الحل
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-[#FEF3C7] text-[#92400e] text-[10px] font-bold rounded-full px-2 py-0.5">
      <Clock3 className="w-3 h-3" /> قيد المراجعة
    </span>
  );
};

const Complaints = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const fetchList = async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/me/complaints`);
      setList(data || []);
    } catch (err) {
      // silent
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const submit = async () => {
    const m = message.trim();
    if (m.length < 3) { toast.error("الرجاء كتابة الشكوى أو الملاحظة"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/complaints`, { message: m });
      toast.success("تم إرسال شكواك للإدارة — سيتمّ مراجعتها قريباً");
      setMessage("");
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الإرسال");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pb-20" dir="rtl">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gradient-to-l from-[#E85D45] to-[#dc2626] text-white shadow-lg">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <button onClick={() => navigate(-1)} data-testid="complaints-back-btn"
            className="bg-white/20 hover:bg-white/30 rounded-full p-2" aria-label="رجوع">
            <ArrowRight className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <h1 className="font-black text-lg">الشكاوي والملاحظات</h1>
          </div>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-3 py-4 space-y-4">
        {/* Customer snapshot card */}
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E85D45]/30">
          <p className="text-[10px] text-[#5C6E64] mb-1">معلوماتك المُرفقة مع الشكوى:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-[#FEF2F2] rounded-lg p-2">
              <p className="text-[9px] text-[#5C6E64]">الاسم</p>
              <p className="font-bold text-[#111814] truncate">{user?.full_name || user?.customer_name || "—"}</p>
            </div>
            <div className="bg-[#FEF2F2] rounded-lg p-2">
              <p className="text-[9px] text-[#5C6E64]">رقم الهاتف</p>
              <p className="font-bold text-[#111814]" dir="ltr">{user?.phone || "—"}</p>
            </div>
            <div className="bg-[#FEF2F2] rounded-lg p-2">
              <p className="text-[9px] text-[#5C6E64]">اسم الماركت</p>
              <p className="font-bold text-[#111814] truncate">{user?.market_name || "—"}</p>
            </div>
            <div className="bg-[#FEF2F2] rounded-lg p-2">
              <p className="text-[9px] text-[#5C6E64]">رقم الايباد</p>
              <p className="font-bold text-[#111814]" dir="ltr">{user?.customer_id || "—"}</p>
            </div>
          </div>
        </div>

        {/* New complaint */}
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]">
          <label className="text-sm font-bold text-[#111814] mb-2 flex items-center gap-1">
            <MessageCircle className="w-4 h-4 text-[#E85D45]" />
            اكتب شكواك أو ملاحظتك للإدارة
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="مثال: تأخّر التوصيل اليوم · مادة ناقصة · سؤال عن الفاتورة..."
            data-testid="complaint-message-input"
            rows={5}
            maxLength={2000}
            className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:border-[#E85D45] focus:outline-none text-sm resize-none"
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-[10px] text-[#5C6E64]">{message.length}/2000</p>
            <button
              onClick={submit}
              disabled={busy || message.trim().length < 3}
              data-testid="complaint-submit-btn"
              className="bg-[#E85D45] hover:bg-[#dc2626] disabled:opacity-50 text-white font-bold rounded-full px-4 py-2 text-sm flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              إرسال للإدارة
            </button>
          </div>
        </div>

        {/* History */}
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6]">
          <h3 className="font-bold text-sm text-[#111814] mb-2">سجل الشكاوي السابقة</h3>
          {loadingList ? (
            <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#E85D45]" /></div>
          ) : list.length === 0 ? (
            <p className="text-center text-xs text-[#5C6E64] py-4">لا توجد شكاوي سابقة.</p>
          ) : (
            <div className="space-y-2">
              {list.map((c) => (
                <div key={c.id} data-testid={`complaint-row-${c.id}`}
                  className="bg-[#F8FAFC] rounded-xl p-2.5 border border-[#E4EBE6]">
                  <div className="flex justify-between items-start gap-2">
                    <p className="text-xs text-[#111814] flex-1 whitespace-pre-wrap">{c.message}</p>
                    <StatusBadge status={c.status} />
                  </div>
                  <p className="text-[9px] text-[#5C6E64] mt-1" dir="ltr">
                    {new Date(c.created_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}
                  </p>
                  {c.admin_reply && (
                    <div className="mt-1.5 bg-[#DCFCE7] rounded-lg p-2 border-r-4 border-[#15803d]">
                      <p className="text-[10px] font-bold text-[#15803d] mb-0.5">ردّ الإدارة:</p>
                      <p className="text-xs text-[#111814] whitespace-pre-wrap">{c.admin_reply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Complaints;
